import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { differenceInDays, isPast } from 'date-fns';
import { api, uploadUrl } from '../api';

// Oversæt enhed via units.* med fallback til selve værdien (custom enheder)
const unitLabel = (t, u) => t(`units.${u || 'stk'}`, u || 'stk');

function LockIcon({ locked, size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
      {/* Lock body with keyhole punched out via evenodd — transparent regardless of theme */}
      <path
        fillRule="evenodd"
        fill="currentColor"
        d="M6,10 L18,10 Q20,10 20,12 L20,20 Q20,22 18,22 L6,22 Q4,22 4,20 L4,12 Q4,10 6,10 Z M14.2,16 A2.2,2.2,0,0,1,9.8,16 A2.2,2.2,0,0,1,14.2,16 Z"
      />
      {locked
        ? <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
        : <path d="M8 10V7a4 4 0 0 1 8 0" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
      }
    </svg>
  );
}

function expiryInfo(item, t) {
  if (item.opened && item.opened_at && item.days_after_opening) {
    const openedDate = new Date(item.opened_at);
    const expiresAt = new Date(openedDate);
    expiresAt.setDate(expiresAt.getDate() + item.days_after_opening);
    const days = differenceInDays(expiresAt, new Date());
    if (days < 0) return { label: t('expiry.openedExpired'), color: 'text-red-400 font-bold' };
    if (days === 0) return { label: t('expiry.openedToday'), color: 'text-red-400 font-bold' };
    if (days <= 3) return { label: t('expiry.openedDaysLeft', { count: days }), color: 'text-orange-400' };
    return { label: t('expiry.openedDaysLeft', { count: days }), color: 'text-yellow-400' };
  }
  if (!item.expiry_date) return null;
  const days = differenceInDays(new Date(item.expiry_date), new Date());
  if (isPast(new Date(item.expiry_date)) && days < 0) return { label: t('expiry.expired'), color: 'text-red-400 font-bold' };
  if (days === 0) return { label: t('expiry.today'), color: 'text-red-400 font-bold' };
  if (days <= 3) return { label: t('expiry.inDays', { count: days }), color: 'text-orange-400' };
  if (days <= 7) return { label: t('expiry.inDays', { count: days }), color: 'text-yellow-400' };
  return { label: new Date(item.expiry_date).toLocaleDateString(), color: 'text-slate-500' };
}

const ALLERGEN_MAP = {
  'milk':                            { label: 'Mælk',       emoji: '🥛' },
  'gluten':                          { label: 'Gluten',     emoji: '🌾' },
  'nuts':                            { label: 'Nødder',     emoji: '🥜' },
  'peanuts':                         { label: 'Jordnødder', emoji: '🥜' },
  'eggs':                            { label: 'Æg',         emoji: '🥚' },
  'soybeans':                        { label: 'Soja',       emoji: '🫘' },
  'fish':                            { label: 'Fisk',       emoji: '🐟' },
  'crustaceans':                     { label: 'Skaldyr',    emoji: '🦐' },
  'sesame-seeds':                    { label: 'Sesamfrø',   emoji: '🌱' },
  'lactose-free':                    { label: 'Laktosefri', emoji: '✓' },
  'vegetarian':                      { label: 'Vegetarisk', emoji: '🥕' },
  'vegan':                           { label: 'Vegansk',    emoji: '🌿' },
};

function AllergenChips({ allergens, labels }) {
  const { t } = useTranslation();
  const tags = [];
  for (const a of (allergens || [])) {
    if (ALLERGEN_MAP[a]) tags.push({ ...ALLERGEN_MAP[a], key: a, type: 'allergen' });
  }
  for (const l of (labels || [])) {
    if (ALLERGEN_MAP[l]) tags.push({ ...ALLERGEN_MAP[l], key: l, type: 'label' });
  }
  if (!tags.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {tags.map(tag => (
        <span key={tag.key} className={`text-xs px-1.5 py-0.5 rounded-full font-medium
          ${tag.type === 'allergen'
            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
            : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'}`}>
          {tag.emoji} {t('allergens.' + tag.key, tag.label)}
        </span>
      ))}
    </div>
  );
}

function formatTotal(items) {
  const first = items[0];
  const unit = first.unit || 'stk';
  const weightPerUnit = parseFloat(first.weight_per_unit) || 0;
  const totalQty = items.reduce((s, i) => s + i.quantity, 0);
  const weightUnits = ['g','kg','ml','l','cl','dl'];
  if (!weightPerUnit || !weightUnits.includes(unit)) return null;
  const total = totalQty * weightPerUnit;
  if (unit === 'ml' || unit === 'l') {
    const ml = unit === 'l' ? total * 1000 : total;
    if (ml >= 1000) return `${parseFloat((ml / 1000).toFixed(2))} L`;
    return `${ml} ml`;
  }
  if (unit === 'g' || unit === 'kg') {
    const g = unit === 'kg' ? total * 1000 : total;
    if (g >= 1000) return `${parseFloat((g / 1000).toFixed(2))} kg`;
    return `${g} g`;
  }
  return null;
}

function worstExpiry(items, t) {
  let worst = null;
  for (const item of items) {
    const e = expiryInfo(item, t);
    if (!e) continue;
    if (!worst) { worst = e; continue; }
    if (e.color.includes('red') && !worst.color.includes('red')) worst = e;
    else if (e.color.includes('orange') && !worst.color.includes('red') && !worst.color.includes('orange')) worst = e;
  }
  return worst;
}

export default function ProductGroup({ items, listType, onTap, onUse, onDelete, onChanged, onSplitOpen, dietWarning }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const first = items[0];
  const totalQty = items.reduce((s, i) => s + i.quantity, 0);
  const canExpand = items.length > 1 || items.some(i => i.quantity > 1);
  const thumbnail = first.images?.[0]?.filename
    ? uploadUrl(first.images[0].filename) : first.thumbnail;
  const worst = worstExpiry(items, t);
  const hasWarning = worst && (worst.color.includes('red') || worst.color.includes('orange'));
  const openedCount = items.filter(i => i.opened).length;

  const toggleOpen = async (item, e) => {
    e.stopPropagation();
    const now = new Date().toISOString().slice(0, 10);
    await api.updateInventory(item.id, {
      quantity: item.quantity,
      location_id: item.location_id || null,
      expiry_date: item.expiry_date || null,
      notes: item.notes || null,
      opened: item.opened ? 0 : 1,
      opened_at: item.opened ? null : now,
    });
    onChanged();
  };

  // Single item with quantity=1: simple row
  if (items.length === 1 && items[0].quantity === 1) {
    const item = items[0];
    const expiry = expiryInfo(item, t);
    return (
      <SingleItemRow item={item} expiry={expiry} thumbnail={thumbnail} listType={listType}
        onTap={() => onTap(item)} onUse={() => onUse(item)}
        onToggleOpen={(e) => toggleOpen(item, e)} dietWarning={dietWarning} />
    );
  }

  return (
    <div className={`border-b border-slate-800 ${hasWarning ? 'border-l-4 border-l-red-500' : ''}`}>
      {/* Group header row */}
      <div className="flex items-center gap-3 px-4 py-3 active:bg-slate-800/50"
        onClick={() => canExpand ? setExpanded(e => !e) : onTap(items[0])}>
        <div className="w-12 h-12 rounded-xl bg-slate-800 flex-shrink-0 overflow-hidden">
          {thumbnail
            ? <img src={thumbnail} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            : <span className="w-full h-full flex items-center justify-center text-2xl">
                {listType === 'freezer' ? '🧊' : listType === 'fridge' ? '🥛' : '🥫'}
              </span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-semibold truncate">{first.name}</p>
            {first.brand && <span className="text-xs text-slate-500 truncate">{first.brand}</span>}
            {dietWarning && <span title="Passer ikke til dine kostvaner" className="text-xs">⚠️</span>}
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            {first.weight_per_unit && ["g","kg","ml","l","cl","dl"].includes(first.unit) && <span className="text-xs text-slate-400">{first.weight_per_unit}{first.unit}</span>}
            {first.location_name && <span className="text-xs text-slate-500">📍 {first.location_name}</span>}
            {openedCount > 0 && <span className="text-xs text-amber-400 inline-flex items-center gap-1"><LockIcon locked={false} size={12} /> {t('storage.openedCount', { count: openedCount })}</span>}
            {worst && <span className={`text-xs ${worst.color}`}>{worst.label}</span>}
          </div>
          <AllergenChips allergens={first.allergens} labels={first.labels} />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="text-right">
            <div>
              <span className="text-2xl font-bold text-white">{totalQty}</span>
              <span className="text-xs text-slate-500 ml-1">{t('units.stk')}</span>
            </div>
            {formatTotal(items) && (
              <div className="text-xs text-slate-400">{formatTotal(items)}</div>
            )}
          </div>
          {canExpand && (
            <span className={`text-slate-400 text-sm transition-transform ${expanded ? 'rotate-90' : ''}`}>›</span>
          )}
        </div>
      </div>

      {/* Expanded individual items */}
      {expanded && (
        <div className="bg-slate-800/30">
          {items.map((item) => {
            const expiry = expiryInfo(item, t);
            return (
              <div key={item.id} className="flex items-center gap-3 px-4 py-2.5 border-t border-slate-800/50">
                <button onClick={(e) => toggleOpen(item, e)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                  style={item.opened
                    ? { background: 'oklch(70% 0.18 75)', border: '1px solid oklch(65% 0.20 70)', color: 'white' }
                    : { background: 'var(--bg-sunken)', border: '1px solid var(--border-default)', color: 'var(--fg-2)' }}>
                  <LockIcon locked={!item.opened} size={14} />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{item.quantity} {unitLabel(t, item.unit)}</span>
                    {item.location_name && <span className="text-xs text-slate-500">📍 {item.location_name}</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    {item.date_added && <span className="text-xs text-slate-600">{t('item.dateAdded')}: {new Date(item.date_added).toLocaleDateString()}</span>}
                    {expiry && <span className={`text-xs ${expiry.color}`}>{expiry.label}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {item.quantity > 1 && !item.opened && (
                    <button onClick={(e) => { e.stopPropagation(); onSplitOpen(item); }}
                      className="h-9 px-2 rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-600/20 dark:text-amber-400 flex items-center justify-center text-xs active:bg-amber-200 dark:active:bg-amber-600/40 border border-amber-300 dark:border-amber-800">
                      <LockIcon locked={false} size={12} /> {t('storage.openOne')}
                    </button>
                  )}
                  {item.quantity === 1 && (
                    <button onClick={(e) => toggleOpen(item, e)}
                      className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors"
                      style={item.opened
                        ? { background: 'oklch(70% 0.18 75)', border: '1px solid oklch(65% 0.20 70)', color: 'white' }
                        : { background: 'var(--bg-sunken)', border: '1px solid var(--border-default)', color: 'var(--fg-2)' }}>
                      <LockIcon locked={!item.opened} size={14} />
                    </button>
                  )}
                  <button onClick={() => onUse(item)}
                    className="w-9 h-9 rounded-lg bg-blue-100 text-blue-700 dark:bg-blue-600/20 dark:text-blue-400 flex items-center justify-center text-sm active:bg-blue-200 dark:active:bg-blue-600/40">
                    −1
                  </button>
                  <button onClick={() => onTap(item)}
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-sm" style={{ background: 'var(--bg-input)', color: 'var(--fg-2)' }}>
                    ✏️
                  </button>
                  {/* Brug alt — sletter fra lager, IKKE fra katalog */}
                  <button onClick={() => onDelete(item)}
                    className="w-9 h-9 rounded-lg bg-green-100 text-green-700 dark:bg-green-600/20 dark:text-green-400 flex items-center justify-center active:bg-green-200 dark:active:bg-green-600/40"
                    title={t('storage.useAll')}>
                    ✓
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SingleItemRow({ item, expiry, thumbnail, listType, onTap, onUse, onToggleOpen, dietWarning }) {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const startX = { current: null };
  const dragging = { current: false };
  const hasWarning = expiry && (expiry.color.includes('red') || expiry.color.includes('orange'));

  return (
    <div className={`relative overflow-hidden border-b border-slate-800 ${hasWarning ? 'border-l-4 border-l-red-500' : ''}`}>
      {/* Swipe actions — kun "Brug alt", ingen slet fra lager her */}
      <div className="absolute right-0 top-0 bottom-0 flex">
        <button onClick={onUse} className="h-full w-28 bg-green-600 flex flex-col items-center justify-center text-white text-xs gap-1 active:bg-green-700">
          <span className="text-xl">✓</span><span>{t('storage.useAll')}</span>
        </button>
      </div>
      <div className="relative bg-slate-800 flex items-center gap-3 px-4 py-3"
        style={{ transform: `translateX(${offset}px)`, transition: dragging.current ? 'none' : 'transform 0.2s' }}
        onTouchStart={e => { startX.current = e.touches[0].clientX; dragging.current = false; }}
        onTouchMove={e => {
          const dx = e.touches[0].clientX - startX.current;
          if (Math.abs(dx) > 5) dragging.current = true;
          setOffset(Math.max(-112, Math.min(0, dx)));
        }}
        onTouchEnd={() => { setOffset(offset < -56 ? -112 : 0); }}
        onClick={() => { if (!dragging.current && offset === 0) onTap(); }}>
        <div className="w-12 h-12 rounded-xl bg-slate-800 flex-shrink-0 overflow-hidden">
          {thumbnail
            ? <img src={thumbnail} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            : <span className="w-full h-full flex items-center justify-center text-2xl">
                {listType === 'freezer' ? '🧊' : listType === 'fridge' ? '🥛' : '🥫'}
              </span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-semibold truncate">{item.name}</p>
            {item.brand && <span className="text-xs text-slate-500">{item.brand}</span>}
            {dietWarning && <span title="Passer ikke til dine kostvaner" className="text-xs">⚠️</span>}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {item.weight_per_unit && ["g","kg","ml","l","cl","dl"].includes(item.unit) && <span className="text-xs text-slate-400">{item.weight_per_unit}{item.unit}</span>}
            {item.location_name && <span className="text-xs text-slate-500">📍 {item.location_name}</span>}
            {expiry && <span className={`text-xs ${expiry.color}`}>{expiry.label}</span>}
          </div>
          {item.date_added && <p className="text-xs text-slate-600 mt-0.5">{t('item.dateAdded')}: {new Date(item.date_added).toLocaleDateString()}</p>}
          <AllergenChips allergens={item.allergens} labels={item.labels} />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={onToggleOpen}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors"
            style={item.opened
              ? { background: 'oklch(70% 0.18 75)', border: '1px solid oklch(65% 0.20 70)', color: 'white' }
              : { background: 'var(--bg-sunken)', border: '1px solid var(--border-default)', color: 'var(--fg-2)' }}>
            <LockIcon locked={!item.opened} size={14} />
          </button>
          <div className="text-right">
            <div>
              <span className="text-2xl font-bold text-white">{item.quantity}</span>
              <span className="text-xs text-slate-500 ml-1">{t('units.stk')}</span>
            </div>
            {formatTotal([item]) && (
              <div className="text-xs text-slate-400">{formatTotal([item])}</div>
            )}
          </div>
          <span className="text-slate-600 text-sm">›</span>
        </div>
      </div>
    </div>
  );
}
